import React, { useEffect, useMemo, useState } from 'react';
import usePersistentLanguage from '../hooks/usePersistentLanguage';
import { useNavigate } from 'react-router-dom';
import { getProducts } from '../api/productApi';
import { getCustomers } from '../api/customerApi';
import { createSalesRequest } from '../api/salesRequestApi';
import styles from './SalesCreateRequest.module.css';
import AppLayout from '../components/AppLayout';
import ProductImage from '../components/ProductImage';

const translations = {
  en: {
    pageHeader: 'Make a New Sales Request',
    explanationText:
      'Choose a customer, select the products required, and submit multi-item requests for manager review.',
    availableProducts: 'Product catalog',
    noImage: 'No Image',
    loadingImage: 'Loading image...',
    imageUnavailable: 'Image unavailable',
    productSKU: 'SKU:',
    productSize: 'Size:',
    customerLabel: 'Select customer',
    selectCustomer: 'Choose a customer',
    searchCustomer: 'Search by name, ID, organization, or phone',
    noMatchingCustomers: 'No customers match your search.',
    noCustomers: 'You do not have any customers yet. Please add a customer first.',
    actionLabel: 'Request action',
    add: 'Add (Inbound)',
    remove: 'Remove (Outbound)',
    requestDetails: 'Item details',
    sortLabel: 'Sort',
    selectSort: 'Select a sort',
    colorLabel: 'Color',
    selectColor: 'Select a color',
    quantityLabel: 'Quantity',
    submissionDateLabel: 'Desired date',
    addItem: 'Add item to request',
    yourItems: 'Items in request',
    product: 'Product',
    sort: 'Sort',
    color: 'Color',
    quantity: 'Quantity',
    submissionDate: 'Desired date',
    unitPrice: 'Unit price',
    lineTotal: 'Line total',
    selectedPrice: 'Selected sort price',
    pricingSummary: 'Pricing summary',
    subtotal: 'Subtotal',
    requestedDiscount: 'Requested discount %',
    discountAmount: 'Discount amount',
    totalAfterDiscount: 'Total after discount',
    paymentDetails: 'Payment details',
    paymentType: 'Payment type',
    cash: 'Cash',
    postpaid: 'Postpaid',
    paymentDueDate: 'Payment due date',
    removeItem: 'Remove',
    submitRequest: 'Submit request',
    customerRequired: 'Please select a customer before submitting the request.',
    errorTitle: 'Error',
    errorFillAll: 'Please fill out all fields before making a request.',
    errorQuantity: 'Quantity must be greater than zero.',
    errorDiscount: 'Requested discount must be between 0 and 100.',
    paymentDueDateRequired: 'Please select a payment due date for postpaid requests.',
    productHint: 'Select a product from the catalog to begin.',
    failedLoadProducts: 'Failed to load products. Please try again.',
    failedCreateRequest: 'Failed to create your request. Please try again.',
    close: 'Close',
    loading: 'Loading...',
    homepage: 'Homepage',
  },
  ar: {
    pageHeader: 'إنشاء طلب مبيعات جديد',
    explanationText:
      'اختر العميل، ثم المنتجات المطلوبة، وأرسل طلبًا متعدد العناصر لمراجعته من قبل المدير.',
    availableProducts: 'كتالوج المنتجات',
    noImage: 'لا توجد صورة',
    loadingImage: 'جارٍ تحميل الصورة...',
    imageUnavailable: 'الصورة غير متاحة',
    productSKU: 'رمز المنتج:',
    productSize: 'الحجم:',
    customerLabel: 'اختر العميل',
    selectCustomer: 'اختر عميلاً',
    searchCustomer: 'ابحث بالاسم أو الرقم أو الشركة أو الهاتف',
    noMatchingCustomers: 'لا يوجد عملاء مطابقون للبحث.',
    noCustomers: 'لا يوجد لديك عملاء بعد. يرجى إضافة عميل أولاً.',
    actionLabel: 'نوع الإجراء',
    add: 'إضافة (وارد)',
    remove: 'سحب (صادر)',
    requestDetails: 'تفاصيل العنصر',
    sortLabel: 'الصنف',
    selectSort: 'اختر الصنف',
    colorLabel: 'اللون',
    selectColor: 'اختر اللون',
    quantityLabel: 'الكمية',
    submissionDateLabel: 'تاريخ التقديم',
    addItem: 'أضف العنصر إلى الطلب',
    yourItems: 'العناصر في الطلب',
    product: 'المنتج',
    sort: 'الصنف',
    color: 'اللون',
    quantity: 'الكمية',
    submissionDate: 'التاريخ المطلوب',
    unitPrice: 'سعر الوحدة',
    lineTotal: 'إجمالي السطر',
    selectedPrice: 'سعر الصنف المختار',
    pricingSummary: 'ملخص السعر',
    subtotal: 'الإجمالي قبل الخصم',
    requestedDiscount: 'نسبة الخصم المطلوبة %',
    discountAmount: 'قيمة الخصم',
    totalAfterDiscount: 'الإجمالي بعد الخصم',
    paymentDetails: 'تفاصيل الدفع',
    paymentType: 'نوع الدفع',
    cash: 'كاش',
    postpaid: 'آجل',
    paymentDueDate: 'تاريخ الاستحقاق',
    removeItem: 'حذف',
    submitRequest: 'تقديم الطلب',
    customerRequired: 'يرجى اختيار عميل قبل إرسال الطلب.',
    errorTitle: 'خطأ',
    errorFillAll: 'يرجى ملء جميع الحقول قبل إنشاء الطلب.',
    errorQuantity: 'يجب أن تكون الكمية أكبر من صفر.',
    errorDiscount: 'يجب أن تكون نسبة الخصم المطلوبة بين 0 و100.',
    paymentDueDateRequired: 'يرجى اختيار تاريخ الاستحقاق للطلبات الآجلة.',
    productHint: 'اختر منتجًا من الكتالوج للبدء.',
    failedLoadProducts: 'فشل في تحميل المنتجات. حاول مرة أخرى.',
    failedCreateRequest: 'فشل في إنشاء الطلب. حاول مرة أخرى.',
    close: 'إغلاق',
    loading: 'جارٍ التحميل...',
    homepage: 'الصفحة الرئيسية',
  },
};

const SalesCreateRequest = () => {
  const [language, setLanguage] = usePersistentLanguage();
  const t = (key) => translations[language][key] || key;
  const toggleLanguage = () => setLanguage((prev) => (prev === 'en' ? 'ar' : 'en'));

  const navigate = useNavigate();
  const token = useMemo(() => localStorage.getItem('authToken'), []);
  const userInfo = useMemo(() => {
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        return {
          userType: payload.userType,
          requesterUsername: payload.username,
        };
      } catch (error) {
        console.error('Error parsing token payload:', error);
        return { userType: null, requesterUsername: null };
      }
    }
    return { userType: null, requesterUsername: null };
  }, [token]);

  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState(null);

  const [action, setAction] = useState('add');
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);

  const [selectedProductId, setSelectedProductId] = useState(null);
  const [selectedSortName, setSelectedSortName] = useState('');
  const [selectedColor, setSelectedColor] = useState('');
  const [itemQuantity, setItemQuantity] = useState('');
  const [itemSubmissionDate, setItemSubmissionDate] = useState('');

  const [items, setItems] = useState([]);
  const [requestedDiscountPercent, setRequestedDiscountPercent] = useState('0');
  const [paymentType, setPaymentType] = useState('cash');
  const [paymentDueDate, setPaymentDueDate] = useState('');

  const selectedProduct = products.find((product) => product._id === selectedProductId);
  const selectedSort = selectedProduct?.sorts?.find((sort) => sort.sortName === selectedSortName);
  const selectedUnitPrice = Number(selectedSort?.price) || 0;
  const numericDiscount = Number(requestedDiscountPercent) || 0;
  const requestSubtotal = items.reduce((sum, item) => sum + (Number(item.lineTotal) || 0), 0);
  const requestDiscountAmount = requestSubtotal * (numericDiscount / 100);
  const requestTotalAfterDiscount = requestSubtotal - requestDiscountAmount;

  const getCustomerLabel = (customer) =>
    customer
      ? `${customer.fullName || customer.name || t('selectCustomer')} (${customer.customerID || '-'})`
      : '';

  const filteredCustomers = useMemo(() => {
    const searchValue = customerSearchTerm.trim().toLowerCase();
    if (!searchValue) {
      return customers;
    }

    return customers.filter((customer) => {
      const customerName = customer.fullName || customer.name || '';
      const searchableValues = [
        customerName,
        customer.customerID,
        `${customerName} (${customer.customerID || '-'})`,
        customer.organizationName,
        customer.phoneNumber,
      ];

      return searchableValues.some((value) =>
        String(value || '').toLowerCase().includes(searchValue)
      );
    });
  }, [customerSearchTerm, customers]);

  const formatCurrency = (value) =>
    new Intl.NumberFormat(language === 'ar' ? 'ar-EG' : 'en-US', {
      style: 'currency',
      currency: 'EGP',
      maximumFractionDigits: 2,
    }).format(Number(value) || 0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [productsResponse, customersResponse] = await Promise.all([
          getProducts(),
          getCustomers(),
        ]);
        setProducts(productsResponse.data);
        setCustomers(customersResponse.data || []);
      } catch (error) {
        console.error('Error loading data for sales request:', error);
        setErrorMessage(t('failedLoadProducts'));
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    // eslint-disable-next-line
  }, [language]);

  const handleAddItem = () => {
    if (!selectedProductId || !selectedSortName || !selectedColor || !itemQuantity || !itemSubmissionDate) {
      setErrorMessage(t('errorFillAll'));
      return;
    }

    const quantity = Number(itemQuantity);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setErrorMessage(t('errorQuantity'));
      return;
    }

    const newItem = {
      product: selectedProduct.SKU,
      productName: selectedProduct.name,
      sortName: selectedSortName,
      color: selectedColor,
      quantity,
      itemSubmissionDate,
      unitPrice: selectedUnitPrice,
      lineTotal: selectedUnitPrice * quantity,
    };

    setItems((prev) => [...prev, newItem]);
    setSelectedProductId(null);
    setSelectedSortName('');
    setSelectedColor('');
    setItemQuantity('');
    setItemSubmissionDate('');
  };

  const handleRemoveItem = (index) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomerId(customer._id);
    setCustomerSearchTerm(getCustomerLabel(customer));
    setIsCustomerDropdownOpen(false);
  };

  const handleSubmitRequest = async () => {
    if (!selectedCustomerId) {
      setErrorMessage(t('customerRequired'));
      return;
    }

    if (items.length === 0) {
      setErrorMessage(t('errorFillAll'));
      return;
    }

    const discountValue = Number(requestedDiscountPercent);
    if (!Number.isFinite(discountValue) || discountValue < 0 || discountValue > 100) {
      setErrorMessage(t('errorDiscount'));
      return;
    }

    if (paymentType === 'postpaid' && !paymentDueDate) {
      setErrorMessage(t('paymentDueDateRequired'));
      return;
    }

    const payload = {
      action,
      items,
      customerId: selectedCustomerId,
      requestedDiscountPercent: discountValue,
      paymentType,
      paymentDueDate: paymentType === 'postpaid' ? paymentDueDate : null,
    };

    try {
	      setLoading(true);
	      await createSalesRequest(payload);
	      setLoading(false);
	      navigate(userInfo.userType === 'sales-manager' ? '/sales-manager' : '/sales-my-requests');
    } catch (error) {
      console.error('Error creating sales request:', error);
      setErrorMessage(t('failedCreateRequest'));
      setLoading(false);
    }
  };

  const closeModal = () => {
    setErrorMessage(null);
  };

  return (
    <AppLayout
      title={t('pageHeader')}
      subtitle={t('explanationText')}
      language={language}
      onToggleLanguage={toggleLanguage}
      userType={userInfo.userType}
      showHome
      homeLabel={t('homepage')}
      contentClassName={styles.page}
      contentProps={{ 'aria-busy': loading }}
    >
      {loading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>{t('loading')}</p>
        </div>
      )}

      <section className={styles.layout}>
        <aside className={styles.catalogSection}>
          <h2>{t('availableProducts')}</h2>
          <div className={styles.catalogGrid}>
            {products.map((product) => {
              return (
                <button
                  type="button"
                  key={product._id}
                  className={`${styles.productCard} ${
                    selectedProductId === product._id ? styles.productCardSelected : ''
                  }`}
                  onClick={() => {
                    setSelectedProductId(product._id);
                    setSelectedSortName('');
                    setSelectedColor('');
                  }}
                >
                  <ProductImage
                    productId={product._id}
                    hasImage={Boolean(product.hasImage)}
                    alt={product.name}
                    className={styles.productImage}
                    placeholderClassName={styles.productPlaceholder}
                    loadingLabel={t('loadingImage')}
                    noImageLabel={t('noImage')}
                    errorLabel={t('imageUnavailable')}
                  />
                  <div className={styles.productInfo}>
                    <h3>{product.name}</h3>
                    <span>{`${t('productSKU')} ${product.SKU}`}</span>
                    <span>{`${t('productSize')} ${product.size}`}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        <section className={styles.builderSection}>
          <div className={styles.builderCard}>
            <div className={styles.fieldGroup}>
              <label>{t('customerLabel')}</label>
              <div className={styles.customerCombobox}>
                <input
                  type="search"
                  value={customerSearchTerm}
                  onChange={(event) => {
                    setCustomerSearchTerm(event.target.value);
                    setSelectedCustomerId('');
                    setIsCustomerDropdownOpen(true);
                  }}
                  onFocus={() => setIsCustomerDropdownOpen(true)}
                  onBlur={() => {
                    window.setTimeout(() => setIsCustomerDropdownOpen(false), 120);
                  }}
                  className={styles.input}
                  placeholder={t('searchCustomer')}
                  disabled={customers.length === 0}
                  autoComplete="off"
                />
                {isCustomerDropdownOpen && customers.length > 0 && (
                  <div className={styles.customerDropdown} role="listbox">
                    {filteredCustomers.length > 0 ? (
                      filteredCustomers.map((customer) => (
                        <button
                          key={customer._id}
                          type="button"
                          className={`${styles.customerOption} ${
                            selectedCustomerId === customer._id ? styles.customerOptionSelected : ''
                          }`}
                          onMouseDown={(event) => event.preventDefault()}
                          onClick={() => handleCustomerSelect(customer)}
                          role="option"
                          aria-selected={selectedCustomerId === customer._id}
                        >
                          <span>{customer.fullName || customer.name || t('selectCustomer')}</span>
                          <small>
                            {[customer.customerID, customer.organizationName, customer.phoneNumber]
                              .filter(Boolean)
                              .join(' | ')}
                          </small>
                        </button>
                      ))
                    ) : (
                      <p className={styles.customerNoResults}>{t('noMatchingCustomers')}</p>
                    )}
                  </div>
                )}
              </div>
              {customers.length === 0 && <p className={styles.helperText}>{t('noCustomers')}</p>}
            </div>

            <div className={styles.fieldGroup}>
              <label>{t('actionLabel')}</label>
              <select
                value={action}
                onChange={(event) => setAction(event.target.value)}
                className={styles.select}
              >
                <option value="add">{t('add')}</option>
                <option value="remove">{t('remove')}</option>
              </select>
            </div>

            <div className={styles.divider}></div>

            <div className={styles.fieldGroup}>
              <h3>{t('requestDetails')}</h3>
              {!selectedProduct && (
                <p className={styles.helperText}>{t('productHint')}</p>
              )}

              {selectedProduct && (
                <div className={styles.detailGrid}>
                  <label className={styles.field}>
                    <span>{t('sortLabel')}</span>
                    <select
                      value={selectedSortName}
                      onChange={(event) => setSelectedSortName(event.target.value)}
                      className={styles.select}
                    >
                      <option value="">{t('selectSort')}</option>
                      {selectedProduct.sorts.map((sortObj) => (
                        <option key={sortObj.sortName} value={sortObj.sortName}>
                          {sortObj.sortName} - {formatCurrency(sortObj.price)}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className={styles.field}>
                    <span>{t('colorLabel')}</span>
                    <select
                      value={selectedColor}
                      onChange={(event) => setSelectedColor(event.target.value)}
                      className={styles.select}
                      disabled={!selectedSortName}
                    >
                      <option value="">{t('selectColor')}</option>
                      {selectedProduct.sorts
                        .find((sort) => sort.sortName === selectedSortName)
                        ?.colors.map((colorOption) => (
                          <option key={colorOption.color} value={colorOption.color}>
                            {colorOption.color}
                          </option>
                        ))}
                    </select>
                  </label>

                  <label className={styles.field}>
                    <span>{t('quantityLabel')}</span>
                    <input
                      type="number"
                      min="1"
                      value={itemQuantity}
                      onChange={(event) => setItemQuantity(event.target.value)}
                      className={styles.input}
                    />
                  </label>

                  <div className={`${styles.field} ${styles.pricePreview}`}>
                    <span>{t('selectedPrice')}</span>
                    <strong>{formatCurrency(selectedUnitPrice)}</strong>
                  </div>

                  <label className={styles.field}>
                    <span>{t('submissionDateLabel')}</span>
                    <input
                      type="date"
                      value={itemSubmissionDate}
                      onChange={(event) => setItemSubmissionDate(event.target.value)}
                      className={styles.input}
                    />
                  </label>
                </div>
              )}

              <button type="button" className={styles.addItemButton} onClick={handleAddItem}>
                {t('addItem')}
              </button>
            </div>

            {items.length > 0 && (
              <div className={styles.itemsList}>
                <h3>{t('yourItems')}</h3>
                <div className={styles.itemsTable}>
                  <div className={styles.itemsTableHeader}>
                    <span>#</span>
                    <span>{t('product')}</span>
                    <span>{t('sort')}</span>
                    <span>{t('color')}</span>
                    <span>{t('quantity')}</span>
                    <span>{t('unitPrice')}</span>
                    <span>{t('lineTotal')}</span>
                    <span>{t('submissionDate')}</span>
                    <span></span>
                  </div>
                  {items.map((item, index) => (
                    <div key={`${item.product}-${index}`} className={styles.itemsRow}>
                      <span>{index + 1}</span>
                      <span>{item.productName || item.product}</span>
                      <span>{item.sortName}</span>
                      <span>{item.color}</span>
                      <span>{item.quantity}</span>
                      <span>{formatCurrency(item.unitPrice)}</span>
                      <span>{formatCurrency(item.lineTotal)}</span>
                      <span>{item.itemSubmissionDate}</span>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => handleRemoveItem(index)}
                      >
                        {t('removeItem')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className={styles.summaryCard}>
              <h3>{t('pricingSummary')}</h3>
              <div className={styles.summaryRow}>
                <span>{t('subtotal')}</span>
                <strong>{formatCurrency(requestSubtotal)}</strong>
              </div>
              <label className={styles.field}>
                <span>{t('requestedDiscount')}</span>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={requestedDiscountPercent}
                  onChange={(event) => setRequestedDiscountPercent(event.target.value)}
                  className={styles.input}
                />
              </label>
              <div className={styles.summaryRow}>
                <span>{t('discountAmount')}</span>
                <strong>{formatCurrency(requestDiscountAmount)}</strong>
              </div>
              <div className={`${styles.summaryRow} ${styles.summaryHighlight}`}>
                <span>{t('totalAfterDiscount')}</span>
                <strong>{formatCurrency(requestTotalAfterDiscount)}</strong>
              </div>
            </div>

            <div className={styles.fieldGroup}>
              <h3>{t('paymentDetails')}</h3>
              <div className={styles.paymentGrid}>
                <label className={styles.field}>
                  <span>{t('paymentType')}</span>
                  <select
                    value={paymentType}
                    onChange={(event) => {
                      setPaymentType(event.target.value);
                      if (event.target.value === 'cash') {
                        setPaymentDueDate('');
                      }
                    }}
                    className={styles.select}
                  >
                    <option value="cash">{t('cash')}</option>
                    <option value="postpaid">{t('postpaid')}</option>
                  </select>
                </label>

                {paymentType === 'postpaid' && (
                  <label className={styles.field}>
                    <span>{t('paymentDueDate')}</span>
                    <input
                      type="date"
                      value={paymentDueDate}
                      onChange={(event) => setPaymentDueDate(event.target.value)}
                      className={styles.input}
                    />
                  </label>
                )}
              </div>
            </div>

            <div className={styles.footerActions}>
              <button
                type="button"
                className={styles.primaryButton}
                onClick={handleSubmitRequest}
                disabled={items.length === 0}
              >
                {t('submitRequest')}
              </button>
            </div>
          </div>
        </section>
      </section>

      {errorMessage && (
        <div className={styles.modalBackdrop}>
          <div className={styles.modalCard}>
            <h2>{t('errorTitle')}</h2>
            <p>{errorMessage}</p>
            <button type="button" className={styles.primaryButton} onClick={closeModal}>
              {t('close')}
            </button>
          </div>
        </div>
      )}
    </AppLayout>
  );
};

export default SalesCreateRequest;
